<template>
  <div>
    <v-field :class="getValidationClass('name')">
      <label>Ime</label>
      <v-input v-model="form.name"></v-input>
      <span class="md-error" v-if="!$v.form.name.required">Vpišite ime</span>
    </v-field>

    <v-field>
      <label>Ime mape (opcijsko)</label>
      <v-input v-model="form.folderName"></v-input>
    </v-field>

    <v-checkbox v-model="form.isVisible">Viden uporabnikom</v-checkbox>

    <v-checkbox v-model="form.isVisibleToAccounts">Viden organizacijam</v-checkbox>

    <v-field :class="getValidationClass('price')">
      <label>Cena</label>
      <v-input v-model="form.price" type="number"></v-input>
      <span class="md-error" v-if="!$v.form.price.required">Vpišite ceno</span>
    </v-field>

    <div class="text-with-preview">
      <v-field class="text-field">
        <label>Opombe (opcijsko, prikazano uporabniku med izpolnjevanjem)</label>
        <v-textarea v-model="form.generalRemarks"></v-textarea>
      </v-field>

      <div class="text-markdown-preview">
        <div class="text-markdown-preview-title">Predogled</div>
        <div class="text-markdown-preview-content" v-html="generalRemarksMarkdownHtml"></div>
      </div>
    </div>

    <v-field>
      <label>SEO opis</label>
      <v-input v-model="form.seoDescription"></v-input>
    </v-field>

    <v-field :class="getValidationClass('keywords')">
      <label>Ključne besede</label>
      <v-input v-model="form.keywords"></v-input>
      <span class="md-error" v-if="!$v.form.keywords.required">Vpišite ključne besede (ločene z vejico)</span>
    </v-field>

    <v-field>
      <label>SEO ključne besede</label>
      <v-input v-model="form.seoKeywords"></v-input>
    </v-field>

    <v-field>
      <label>Opis</label>
      <v-input v-model="form.description"></v-input>
    </v-field>

    <v-field>
      <label>Relevantne določbe</label>
      <v-input v-model="form.stipulations"></v-input>
    </v-field>

    <v-field>
      <label>Skrbnik</label>
      <v-input v-model="form.owner"></v-input>
    </v-field>

    <v-field>
      <label for="accountIds">Vidno računom</label>
      <r-searchable-select v-model="form.accountIds" :options="accounts.map(a => ({ value: a.id, label: a.name }))" />
    </v-field>

    <v-checkbox v-model="form.allowUserAttachments">Dovoli dodajanje prilog</v-checkbox>

    <div class="md-subheading nested-form-heading">Priloge dokumenta</div>
    <div class="nested-form">
      <div v-for="(attachment, index) in this.form.attachments" :key="attachment.frontendId" :class="getNestedFormClass(index)">
        <br />
        <div :style="{ display: 'flex', 'justify-content': 'space-between' }">
          <div class="md-subheading">priloga {{ index + 1 }}</div>
          <v-button size="small" class="md-icon-button" @click="removeAttachment(index)"><v-icon>close</v-icon></v-button>
        </div>
        <div>
          <v-field>
            <label>Datoteka</label>
            <r-file-upload
              :model="attachment.fileModel"
              :file-upload-prefix="'document_attachments'"
              :accepts="['application/pdf']"
              @change="handleFileChange($event, index)"
            />
          </v-field>
        </div>
      </div>
      <br />
      <v-button size="small" @click="addAttachment">DODAJ PRILOGO</v-button>
      <md-divider />
    </div>
  </div>
</template>

<script>
import snarkdown from 'snarkdown'
import { required } from 'vuelidate/lib/validators'
import ValidationMixin from '../../../shared/ValidationMixin'
import AutocompleteFixMixin from '../../../shared/AutocompleteFixMixin'
import VCheckbox from 'design-system/VCheckbox.vue'
import VField from 'design-system/VField.vue'
import VInput from 'design-system/VInput.vue'
import VTextarea from 'design-system/VTextarea.vue'
import VButton from 'design-system/VButton.vue'
import VIcon from 'design-system/VIcon.vue'
import RFileUpload from 'design-system/components/RFileUpload.vue'
import RSearchableSelect from 'design-system/components/RSearchableSelect.vue'

export default {
  components: {
    VCheckbox,
    VField,
    VInput,
    VTextarea,
    VButton,
    VIcon,
    RFileUpload,
    RSearchableSelect,
  },
  data () {
    return {
      form: {
        name: null,
        price: null,
        keywords: null,
        seoKeywords: null,
        description: null,
        seoDescription: null,
        stipulations: null,
        owner: null,
        generalRemarks: null,
        folderName: null,
        isVisible: false,
        isVisibleToAccounts: false,
        allowUserAttachments: false,
        accountIds: [],
        attachments: [],
      },
    }
  },
  props: {
    document: { type: Object, required: false },
    accounts: { type: Array, required: true },
  },
  mixins: [ValidationMixin, AutocompleteFixMixin],
  created () {
    if (!this.document) {
      return
    }
    this.form.name = this.document.name
    this.form.price = this.document.price
    this.form.keywords = this.document.keywords
    this.form.seoKeywords = this.document.seoKeywords
    this.form.description = this.document.description
    this.form.seoDescription = this.document.seoDescription
    this.form.stipulations = this.document.stipulations
    this.form.owner = this.document.owner
    this.form.generalRemarks = this.document.generalRemarks
    this.form.folderName = this.document.folderName
    this.form.isVisible = this.document.isVisible ?? this.form.isVisible
    this.form.isVisibleToAccounts = this.document.isVisibleToAccounts ?? this.form.isVisibleToAccounts
    this.form.allowUserAttachments = this.document.allowUserAttachments ?? this.form.allowUserAttachments
    this.form.accountIds = this.document.accounts ? this.document.accounts.map(a => a.id) : []

    if (this.document.documentAttachments) {
      this.form.attachments = this.document.documentAttachments.map(attachment => ({
        frontendId: Math.random().toString(36),
        name: attachment.name,
        fileModel: {
          name: attachment.name,
          blobHash: attachment.blobHash,
          type: attachment.contentType,
        },
        blobHash: attachment.blobHash,
        contentType: attachment.contentType,
      }))
    }
  },
  watch: {
    form: {
      handler (newFormValue) {
        this.$emit('change', {
          ...this.document,
          ...newFormValue,
        })
      },
      deep: true,
    },
  },
  computed: {
    generalRemarksMarkdownHtml () {
      if (!this.form.generalRemarks) {
        return ''
      }
      return snarkdown(this.form.generalRemarks)
    },
  },
  methods: {
    runAllValidations () {
      this.$v.$touch()
    },
    isValid () {
      return !this.$v.$invalid
    },
    addAttachment () {
      const newAttachment = {
        frontendId: Math.random().toString(36),
        name: '',
        fileModel: {},
        blobHash: null,
        contentType: null,
      }
      this.form.attachments.push(newAttachment)
    },
    removeAttachment (index) {
      this.form.attachments.splice(index, 1)
    },
    handleFileChange (fileModel, index) {
      if (fileModel && fileModel.blobHash) {
        this.form.attachments[index].name = fileModel.name
        this.form.attachments[index].blobHash = fileModel.blobHash
        this.form.attachments[index].contentType = fileModel.type
      } else {
        this.form.attachments[index].name = ''
        this.form.attachments[index].blobHash = null
        this.form.attachments[index].contentType = null
      }
    },

    getNestedFormClass (index) {
      return {
        'nested-form-item': true,
        'nested-form-item--last': index === this.form.attachments.length - 1,
      }
    },
  },
  validations: {
    form: {
      name: { required },
      price: { required },
      keywords: { required },
      attachments: {
        $each: {
          blobHash: { required },
        },
      },
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../markdown_preview';
</style>
